// One place for the "% change fill" look shared by PnlPerformanceMini (SVG)
// and PercentBaselineChart (lightweight-charts BaselineSeries): 0% baseline,
// green line + fill above, red line + fill below.
import type { PerformancePoint } from '../api/client'

export const PCT_UP = '#4caf50'
export const PCT_DOWN = '#f85149'

export function fmtPct(v: number | null): string {
  if (v == null || Number.isNaN(v)) return '—'
  const s = v.toFixed(2) + '%'
  return v > 0 ? '+' + s : s
}

export function pctClass(v: number | null): '' | 'positive' | 'negative' {
  if (v == null || Number.isNaN(v)) return ''
  return v >= 0 ? 'positive' : 'negative'
}

/** Options for chart.addSeries(BaselineSeries, percentBaselineOptions()). */
export function percentBaselineOptions() {
  return {
    baseValue: { type: 'price' as const, price: 0 },
    topLineColor: PCT_UP,
    topFillColor1: 'rgba(76,175,80,0.45)',
    topFillColor2: 'rgba(76,175,80,0.02)',
    bottomLineColor: PCT_DOWN,
    bottomFillColor1: 'rgba(248,81,73,0.02)',
    bottomFillColor2: 'rgba(248,81,73,0.45)',
    lineWidth: 2 as const,
    priceFormat: { type: 'custom' as const, minMove: 0.01, formatter: (p: number) => fmtPct(p) },
    lastValueVisible: true,
    priceLineVisible: false,
  }
}

/**
 * SVG paths for a mini sparkline of pct values. The vertical range is
 * symmetric around 0 so the zero line is meaningful; `above` and `below`
 * are the closed fill regions clipped to each side of zero.
 */
export function sparkAreaPaths(points: PerformancePoint[], width: number, height: number):
  { line: string; above: string; below: string; zeroY: number } {
  if (points.length < 2) return { line: '', above: '', below: '', zeroY: height / 2 }
  const maxAbs = Math.max(0.01, ...points.map(p => Math.abs(p.pct)))
  const zeroY = height / 2
  const y = (pct: number) => zeroY - (pct / maxAbs) * (height / 2)
  const xStep = width / (points.length - 1)
  const xy = points.map((p, i) => [i * xStep, y(p.pct)] as const)
  const fmt = (n: number) => Number.isInteger(n) ? String(n) : n.toFixed(2)
  const line = xy.map(([x, yy], i) => `${i === 0 ? 'M' : 'L'}${fmt(x)},${fmt(yy)}`).join(' ')
  const aboveXy = xy.map(([x, yy]) => [x, Math.min(yy, zeroY)] as const)
  const belowXy = xy.map(([x, yy]) => [x, Math.max(yy, zeroY)] as const)
  const closed = (pts: readonly (readonly [number, number])[]) =>
    pts.map(([x, yy], i) => `${i === 0 ? 'M' : 'L'}${fmt(x)},${fmt(yy)}`).join(' ') +
    ` L${fmt(width)},${fmt(zeroY)} L0,${fmt(zeroY)} Z`
  return { line, above: closed(aboveXy), below: closed(belowXy), zeroY }
}
